/**
 * MER-532: the multi-membership "which workspace do we land on?" decision as a
 * PURE function, so the node:test suite exercises the whole matrix without a
 * browser (same pattern as rootRoute.mjs / homePath.mjs). RootResolver and the
 * "Choose your workspace" interstitial both render from this; the async
 * tenant_members read (getUserMemberships) and the tenant-context reads
 * (lib/tenantContext) stay in the callers.
 *
 * Precedence (each wins over the ones below):
 *   1. no memberships            -> { kind: "none" }   (unprovisioned: caller
 *                                                        falls back to ComingSoon)
 *   2. activeSlug, still valid   -> { kind: "home", slug }   (the choice the
 *                                       picker set for THIS session; honored
 *                                       first so a picked workspace sticks)
 *   3. exactly one membership    -> { kind: "home", slug }   (unambiguous; never
 *                                       show a picker for a single tenant)
 *   4. defaultSlug, still valid  -> { kind: "home", slug }   (an explicit
 *                                       default_tenant; see note below)
 *   5. >1 and no active/default  -> { kind: "picker" }       (ask the user)
 *
 * Both activeSlug and defaultSlug are validated against the membership list, so
 * a stale or revoked choice (a tenant the user no longer belongs to) is ignored
 * rather than routing to a dead workspace.
 *
 * default_tenant note: this layer HONORS an explicit default when one is
 * present in the tenant-context. The durable WRITER of that default (a settings
 * / admin surface, or remembering the last picked workspace across sessions) is
 * the deferred follow-up that lands after MER-528; until then the picker sets
 * only the per-session active workspace and a default simply stays unset.
 */

/**
 * @param {{
 *   slugs: string[],
 *   activeSlug?: string | null,
 *   defaultSlug?: string | null,
 * }} input
 * @returns {{ kind: "none" }
 *   | { kind: "home", slug: string }
 *   | { kind: "picker" }}
 */
export function resolveWorkspace(input) {
  const slugs = input.slugs ?? [];
  const activeSlug = input.activeSlug ?? null;
  const defaultSlug = input.defaultSlug ?? null;

  if (slugs.length === 0) return { kind: "none" };
  if (activeSlug && slugs.includes(activeSlug)) return { kind: "home", slug: activeSlug };
  if (slugs.length === 1) return { kind: "home", slug: slugs[0] };
  if (defaultSlug && slugs.includes(defaultSlug)) return { kind: "home", slug: defaultSlug };
  return { kind: "picker" };
}
